import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useCombobox } from "downshift";

import { getString, getSuggestions, sortedArrayByItem } from "../../utilities";
import { getAllGames } from "../../utilities/games";
import { getPrintableCategory } from "../../utilities/printables";
import MagnifySearchField from "../../../assets/svg/magnifySearchField.svg";
import { routeCodes } from "../../config/routes";
import { getContentType, getLangCode, getSearchParamString } from "../../config";
import { contentTypes, language, SEARCH_SUGGESTIONS_LIMIT } from "../../config/constants";
import faqStrings from "../../strings/faq";
import { dataSelector, loadingSelector } from "../../redux/slices/dataSlice";
import { useClickOutside } from "../../hooks";
import ArrowRight from "../../../assets/svg/searchResultsArrow.svg";

const SEE_ALL_RESULTS_ITEM = { type: "seeAllResults" };

export default function SearchField() {
  const [searchData, setSearchData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);

  const loading = useSelector(loadingSelector);
  const data = useSelector(dataSelector);
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const toggleRef = useClickOutside(() => setIsMobileSearchActive(false));
  const langCode = getLangCode();

  const onSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value, searchData, "label"));
  };

  const limitedSuggestions = suggestions.slice(0, SEARCH_SUGGESTIONS_LIMIT);
  const showSeeAllResults = suggestions.length > SEARCH_SUGGESTIONS_LIMIT;
  const comboboxItems = showSeeAllResults ? [...limitedSuggestions, SEE_ALL_RESULTS_ITEM] : limitedSuggestions;

  const {
    highlightedIndex,
    isOpen,
    inputValue,
    closeMenu,
    getInputProps,
    getItemProps,
    getMenuProps,
    selectItem,
    setInputValue,
  } = useCombobox({
    items: comboboxItems,
    // From docs: This callback must include a null check: it is invoked with null whenever the user abandons input via <Esc>.
    itemToString: (item) => {
      if (!item || item.type === "seeAllResults") {
        return "";
      }
      return item.label ?? "";
    },
    // Go to search page
    onStateChange: (changes) => {
      switch (changes.type) {
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (changes.selectedItem?.type === "seeAllResults") {
            selectItem(null);
            goToSearchPage();
            break;
          }
          if (changes.selectedItem) {
            const { label, path, type } = changes.selectedItem;
            if ([contentTypes.faq, contentTypes.standard].includes(type)) {
              handleSearchSubmit(type, label);
            } else {
              let prefix = "/";
              if (type === contentTypes.game) {
                prefix = routeCodes.GAMES;
              } else if (type === contentTypes.printable) {
                prefix = routeCodes.PRINTABLES;
              } else if (type === contentTypes.blog) {
                prefix = routeCodes.BLOG;
              }
              const location = {
                pathname: `${prefix}${path}`,
              };

              navigate(location);
              selectItem(null);
              setInputValue(label);
            }
          } else {
            handleSearchSubmit(undefined, inputValue);
          }
          setIsMobileSearchActive(false);
          break;
        default:
          break;
      }
    },
    // Get suggestions
    onInputValueChange: ({ inputValue }) => {
      onSuggestionsFetchRequested({ value: inputValue });
    },
  });

  // when the suggestion is selected

  const handleSearchSubmit = (type, value) => {
    closeMenu();
    const cleanValue = value.trim();
    let filterType = type || contentTypes.all;
    filterType = getContentType(filterType, { langCode });
    const termParamString = getSearchParamString("term");
    const typeParamString = getSearchParamString("type");

    const location = {
      pathname: routeCodes.SEARCH,
      search: `?${termParamString}=${encodeURIComponent(cleanValue)}&${typeParamString}=${filterType}`,
    };
    navigate(location);
  };

  useEffect(() => {
    if (!loading && data) {
      const nextSearchData = [];

      const setData = (arry, labelKey, pathKeys, type) => {
        const unsorted = arry.reduce((current, obj) => {
          const next = current;
          const label = obj[labelKey];
          let path = null;
          for (let i = 0; i < pathKeys.length; ++i) {
            path = obj[pathKeys[i]];
            if (path !== null && path !== undefined) {
              break;
            }
          }
          const dataObj = { label, path, type };

          // handle categories of type
          // suggestions might have specific icon artwork or actions
          if (type === contentTypes.printable) {
            dataObj.category = getPrintableCategory(obj.categories);
          }

          next.push(dataObj);
          return next;
        }, []);
        const sorted = sortedArrayByItem(unsorted, "label");
        nextSearchData.push(...sorted);
      };

      if (data.games) {
        setData(getAllGames(data.games), "fullname", ["localeShortname", "shortname"], contentTypes.game);
      }

      if (data.printables) {
        setData(data.printables, "fullname", ["localeShortname", "shortname"], contentTypes.printable);
      }

      if (data.posts) {
        setData(data.posts, "title", ["id"], contentTypes.blog);
      }

      if (data.standards) {
        setData(data.standards, "tag", ["tag"], contentTypes.standard);
      }

      // faq not in startup data, in strings data
      faqStrings.sections.forEach((section) => {
        setData(section.data, "question", ["question"], contentTypes.faq);
      });

      setSearchData(nextSearchData);
    }
  }, [data, loading]);

  useEffect(() => {
    setIsMobileSearchActive(false);
    closeMenu();
  }, [currentLocation]);

  // Use your imagination to render suggestions.
  const renderSuggestion = (suggestion) => {
    const typeCatStyle = suggestion.category || suggestion.type;
    return (
      <div className={`header-search-suggestion${typeCatStyle ? ` header-search-${typeCatStyle}-suggestion` : ""}`}>
        <span className="header-search-suggestion-text">{suggestion.label}</span>
      </div>
    );
  };

  const handleMobileSearchToggle = () => {
    setIsMobileSearchActive((prev) => !prev);
  };

  const goToSearchPage = () => {
    setIsMobileSearchActive(false);
    handleSearchSubmit(undefined, inputValue);
  };

  const renderSuggestions = () => {
    if (!isOpen) {
      return;
    }
    return (
      <>
        {limitedSuggestions.map((item, index) => {
          return (
            <li
              data-testid="search-suggestion"
              className={`suggestion${highlightedIndex === index ? " highlighted" : ""}`}
              key={JSON.stringify(item)}
              {...getItemProps({ item, index })}
            >
              {renderSuggestion(item)}
            </li>
          );
        })}
        {showSeeAllResults && (
          <li
            key="see-all-results"
            className="suggestion"
            {...getItemProps({ item: SEE_ALL_RESULTS_ITEM, index: limitedSuggestions.length })}
          >
            <button
              className={`see-all-results${highlightedIndex === limitedSuggestions.length ? " highlighted" : ""}`}
              type="button"
            >
              {getString("search.seeAll")}
              <ArrowRight />
            </button>
          </li>
        )}
      </>
    );
  };

  return (
    <div ref={toggleRef} className="header-search-interface">
      <div className="mobile-search-toggle">
        <button
          type="button"
          onClick={handleMobileSearchToggle}
          className={`sub-menu-title ${isMobileSearchActive ? "active" : ""}`}
        >
          <span>{getString("search.mobileToggle")}</span>
          <MagnifySearchField className="search-magnify" />
        </button>
      </div>
      <div
        className={`header-search ${isMobileSearchActive ? "active" : ""} ${langCode === language.la ? "la-header-search" : ""}`}
      >
        <div className="header-search-container">
          <input placeholder={getString("search.cta", { replace: [getString("abcya.0")] })} {...getInputProps()} />
        </div>
        <ul {...getMenuProps()} className="search-suggestions-container dark-cyan-fill suggestions-list">
          {renderSuggestions()}
        </ul>
        <button data-testid="header-search-button" type="button" className="search-submit" onClick={goToSearchPage}>
          <MagnifySearchField className="search-magnify" />
        </button>
      </div>
    </div>
  );
}
